import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { ConfirmationDialogType } from "@comunicare/comunicare-dashboard-modals";
import { FileLogger, Tools } from "@comunicare/comunicare-helper";
import { ICareplan, IKnowledgesCriteria, ILink2Careplan, ITranslation, VersioningStatus } from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { EMPTY, forkJoin, Observable, of, Subject } from "rxjs";
import { catchError, defaultIfEmpty, first, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { KnowledgeCriteriaService } from "src/app/providers/knowledge-criteria-service";
import { LanguagesService } from "src/app/providers/languages.service";
import { UserService } from "src/app/providers/user.service";
import { CareplanEditorService } from "../../domain/careplan-editor.service";
import { CareplanPublicationDialogComponent } from "./components/careplan-publication-dialog/careplan-publication-dialog.component";

@Component({
  selector: "app-careplan-editor-page",
  templateUrl: "./careplan-editor-page.component.html",
  styleUrls: ["./careplan-editor-page.component.scss"],
})
export class CareplanEditorPageComponent implements OnInit, OnDestroy {
  private onDestroy$ = new Subject<void>();
  public activeTabIndex = 0;

  //  Must match the route defined in the careplan-editor-page routing module
  public tabs: string[] = ["general", "questionnaires", "observations", "vitalSigns", "knowledge", "faq", "consent", "communications"];
  public careplanTemplate: ICareplan;
  public availableLangs: ITranslation[];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public careplanEditorService: CareplanEditorService,
    private languagesService: LanguagesService,
    public translateService: TranslateService,
    private snackBar: MatSnackBar,
    private knowledgeCriteriaService: KnowledgeCriteriaService,
    private dialog: MatDialog,
    private userService: UserService
  ) {}

  public ngOnInit(): void {
    this.careplanEditorService.reset();
    this.selectTabBasedOnURL();
    const careplanRef = this.route.snapshot.params.id;
    // The link2careplan draft must exist before the careplan template is loaded: when there is no draft yet, the creation
    // of the knowledge criteria drafts adds their knowledge to the link2careplan draft (and fails if it does not exist yet)
    this.getLink2Careplan(careplanRef, true)
      .pipe(
        catchError((err) => {
          FileLogger.error("CareplanEditorPage - getLink2Careplan", "Error while getting/creating the link2careplan draft", err);
          return of(undefined);
        }),
        defaultIfEmpty(undefined),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => this.getCareplanTemplate(careplanRef, true));
    this.languagesService
      .list()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((languages) => {
        this.availableLangs = languages;
      });
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public selectTab(event: number): void {
    if (this.careplanEditorService.careplanTemplateForm.valid) {
      this.activeTabIndex = event;
      const activeTab = this.tabs[this.activeTabIndex];

      // Extract the careplan ID from the current route
      const careplanId = this.route.snapshot.paramMap.get("id");

      // Construct the route parameters based on the selected tab and careplan ID
      const routeParams = ["careplanEditor", careplanId, activeTab];

      // If 'general' tab is selected, add 'visualView' to the route parameters
      if (activeTab === "general") {
        routeParams.push("visualView");
      }

      // Navigate to the corresponding route based on the selected tab
      this.router.navigate(routeParams);
    } else {
      this.careplanEditorService.showInvalidFormError();
    }
  }

  /**
   * Selects the tab based on the current URL path when navigating manually via the browser's URL input.
   * It retrieves the last segment of the path from the URL and sets `activeTabIndex` accordingly.
   */
  public selectTabBasedOnURL(): void {
    const currentPath = window.location.pathname;
    const urlSegments = currentPath.split("/");
    const pathname = urlSegments.pop();
    this.activeTabIndex = this.tabs.indexOf(pathname);
  }

  /**
   * Retrieves the care plan template based on the provided reference and update the currentCareplanTemplate for this component and inside the careplanEditorService.
   * @param reference - The reference to the care plan.
   */
  public getCareplanTemplate(reference: string, withDraft: boolean): void {
    const availableLangs$ = this.languagesService.list().pipe(takeUntil(this.onDestroy$));
    const careplanTemplate$ = this.careplanEditorService.getCareplanTemplate(reference, withDraft).pipe(first());

    forkJoin([availableLangs$, careplanTemplate$])
      .pipe(
        switchMap(([lang, careplanTemplate]) => {
          this.availableLangs = lang;
          if (!Tools.isDefined(careplanTemplate.draft)) {
            // no draft, need to create one
            const missingPermissions = this.missingPermissionsToCopyKnowledgeCriteria();
            if (missingPermissions.length) {
              // a draft without the copy of the knowledge criteria would retire all the active criteria when it is published
              FileLogger.warn(
                "CareplanEditorPage",
                `User does not have access to: ${missingPermissions.join(", ")}. The draft of ${reference} is not created`
              );
              this.router.navigate(["/careplansList"]);
              this.snackBar.open(this.translateService.instant("api.errors.ACCESS DENIED"), "ok", {
                duration: 5000,
              });
              return EMPTY;
            }
            return this.careplanEditorService.createCareplanTemplate(careplanTemplate.published).pipe(
              switchMap((careplanTemplateCreated) => {
                return this.createKnowledgeCriteriaDraftFromPublished(reference).pipe(
                  switchMap(() => {
                    return this.initForm(careplanTemplateCreated);
                  })
                );
              }),
              catchError((err) => {
                FileLogger.error("CareplanEditorPage - createCareplanTemplate", "Error while creating careplan template", err);
                this.router.navigate(["/careplansList"]);
                this.snackBar.open(this.translateService.instant("api.errors.unknowerror"), "ok", {
                  duration: 5000,
                });

                return EMPTY;
              })
            );
          } else if (CareplanEditorService.hasSharedEditorTerms(careplanTemplate.draft)) {
            // Draft created before the "/draft" terms: it still shares translations with the published versions.
            // Saving it makes the server copy them to terms owned by the draft
            return this.careplanEditorService.updateCareplanTemplate(careplanTemplate.draft).pipe(
              first(),
              catchError((err) => {
                FileLogger.error("CareplanEditorPage - updateCareplanTemplate", "Error while copying the translations of the draft", err);
                return of(careplanTemplate.draft);
              }),
              switchMap((draft) => this.initForm(draft))
            );
          } else {
            // Draft already exists, initialize form with draft
            return this.initForm(careplanTemplate.draft);
          }
        })
      )
      .subscribe();
  }

  /**
   * The knowledge criteria drafts are copied from the published ones (and added to the link2careplan draft) when the careplan draft is created
   */
  private missingPermissionsToCopyKnowledgeCriteria(): string[] {
    const permissions: [string, string][] = [
      ["dashboard/knowledgesCriteria", "GET"],
      ["dashboard/knowledgesCriteria", "POST"],
      ["dashboard/link2careplan", "GET"],
      ["dashboard/link2careplan", "POST"],
    ];
    return permissions
      .filter(([routeName, method]) => !this.userService.isAuthorizedSync(null, routeName, method))
      .map(([routeName, method]) => method + " " + routeName);
  }

  public createKnowledgeCriteriaDraftFromPublished(reference: string): Observable<IKnowledgesCriteria[]> {
    return this.knowledgeCriteriaService.list(null, null, reference, null).pipe(
      first(),
      switchMap((knowledgeCriteria) => {
        // Only create the missing drafts (the server refuses to create a draft that already exists for the same knowledge and activity):
        //  - drafts may already exist even if the careplan template has no draft (e.g. interrupted publication)
        //  - the published criteria may contain duplicates
        const criteriaKey = (kc: IKnowledgesCriteria) => kc.knowledgeIdentifier?.value + "|" + (kc.activityReference?.reference ?? "");
        const existingKeys = new Set((knowledgeCriteria.draft ?? []).map(criteriaKey));
        if (existingKeys.size) {
          FileLogger.warn(
            "CareplanEditorPage",
            `${existingKeys.size} knowledge criteria draft(s) already exist for ${reference}: they are kept`
          );
        }
        const criteriaToCreate = (knowledgeCriteria.published ?? []).filter((kc) => {
          const key = criteriaKey(kc);
          if (existingKeys.has(key)) return false;
          existingKeys.add(key);
          return true;
        });

        if (criteriaToCreate.length === 0) {
          // Return an observable that emits an empty array because forJoin will not emit if createObservables is empty
          return of([]);
        }

        // Map over the published knowledge criteria and create draft versions
        const createObservables = criteriaToCreate.map((kc) => {
          // Exclude publicationDate and _id from kc with object destructuring
          const { publicationDate: _publicationDate, _id, ...draftKC } = kc;
          draftKC.versioningStatus = VersioningStatus.DRAFT;
          return this.knowledgeCriteriaService.create(draftKC).pipe(first());
        });
        // Return an observable that completes when all drafts are created
        return forkJoin(createObservables);
      })
    );
  }

  private initForm(careplanTemplate: ICareplan) {
    this.careplanEditorService.currentCareplanTemplate = careplanTemplate;
    this.careplanTemplate = careplanTemplate;
    this.careplanEditorService.initCareplanTemplateForm(careplanTemplate);
    return this.careplanEditorService.initTraductions(this.availableLangs.map((l) => l.term)).pipe(
      tap(() => {
        this.careplanEditorService.setCareplanTemplateReady(true);
      })
    );
  }

  /**
   * Retrieves the link2careplan based on the provided careplan ID and populate the link2CareplanForm with the retrieved data
   * @param careplanId - The ID of the careplan.
   */
  public getLink2Careplan(careplanId: string, withDraft: boolean): Observable<ILink2Careplan> {
    return this.careplanEditorService.getLink2Careplan(careplanId, withDraft).pipe(
      // take (not first): the service emits nothing if the user is not authorized
      take(1),
      switchMap((link2careplan) => {
        if (!Tools.isDefined(link2careplan.draft)) {
          // no draft, need to create one
          return this.careplanEditorService.createLink2Careplan(link2careplan.published).pipe(first());
        }
        return of(link2careplan.draft);
      }),
      tap((link2careplanDraft) => {
        // Populate the form
        this.careplanEditorService.link2CareplanForm.patchValue(link2careplanDraft);
        this.careplanEditorService.setLink2CareplanReady(true);
      })
    );
  }

  public save(close?: boolean): void {
    this.careplanEditorService.careplanTemplateForm.markAllAsTouched();
    this.careplanEditorService.save(false, close);
  }

  public publish(): void {
    this.dialog.open(CareplanPublicationDialogComponent, {
      width: "600px",
      data: {
        message: this.translateService.instant("page.careplanEditor.warning.publish"),
        type: ConfirmationDialogType.CONFIRM,
      },
    });
  }
}
